<template>
  <b-card>
    <b-row>
      <b-col md="6">
        <ZonesList @zone-selected="selectedZone = $event" />
      </b-col>
      <b-col
        v-if="selectedZone"
        md="6"
      >
        <ServicesList
          :selected-zone="selectedZone"
          @service-selected="selectedService = $event"
        />
      </b-col>
    </b-row>
    <base-overlay :show="versionsLoading">
      <div v-if="selectedZone && selectedService">
        <b-table
          :items="getVersionsList"
          responsive
          :fields="tableColumns"
          show-empty
          empty-text="No matching versions found"
          selectable
          :per-page="perPage"
          :current-page="currentPage"
          @row-clicked="evt => $router.push(`/versions/edit/${evt.id}`)"
        >
          <template #cell(createdAt)="data">
            {{ data.item.createdAt | formatDate }}
          </template>
          <template #cell(effectiveDateTime)="data">
            {{ data.item.effectiveDateTime | formatDate }}
          </template>
          <template #cell(published)="data">
            {{ data.item.published ? 'Yes': 'No' }}
          </template>
          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
              @click.stop
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item @click.stop="confirmDeleteVersion(data.item)">
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>

            <!-- Pagination -->
            <b-col
              cols="12"
              sm="12"
              class="d-flex align-items-center justify-content-between"
            >
              <b-form-group
                class="mb-0"
              >
                <label class="d-inline-block text-sm-left mr-50">Per page</label>
                <b-form-select
                  id="perPageSelect"
                  v-model="perPage"
                  size="sm"
                  :options="perPageOptions"
                  class="w-50"
                />
              </b-form-group>
              <b-pagination
                v-model="currentPage"
                :total-rows="totalVersions"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </div>
    </base-overlay>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirm Deletion"
      @hide="selectedVersionToDelete = null"
    >
      <p v-if="selectedVersionToDelete">
        Are you sure you want to delete version <strong>{{ selectedVersionToDelete.label }}</strong>?
      </p>
      <p class="text-muted">This action cannot be undone.</p>
      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="showDeleteModal = false"
        >
          Cancel
        </b-button>
        <b-button
          :disabled="deleting"
          variant="danger"
          @click="deleteVersion"
        >
          <b-spinner
            v-if="deleting"
            small
          />
          Delete
        </b-button>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BTable, BPagination, BFormSelect, BFormGroup, BRow, BCol, BCard,
  BDropdown, BDropdownItem, BModal, BSpinner, BButton,
} from 'bootstrap-vue'
import { watch, ref } from '@vue/composition-api'
import useRegisterStoreModule from '@/hooks/useRegisterStoreModule'
import versionStore from '../store'
import useVersionsList from '../hooks/useVersionsList'
import ZonesList from '../components/ZonesList.vue'
import ServicesList from '../components/ServicesList.vue'
import axiosIns from '@/libs/axios'
import { errorNotification, successNotification } from '@/utils/toast-notifications'

export default {
  components: {
    BTable,
    BFormSelect,
    BPagination,
    ZonesList,
    ServicesList,
    BFormGroup,
    BRow,
    BCard,
    BCol,
    BDropdown,
    BDropdownItem,
    BModal,
    BSpinner,
    BButton,
  },
  setup(props, ctx) {
    useRegisterStoreModule('versions', versionStore)
    const { versionsLoading, getVersionsList, fetchVersions, ...rest } = useVersionsList(ctx)

    watch(versionsLoading, () => {
      ctx.emit('versions-loading-state', versionsLoading.value)
    }, { immediate: true })

    // Delete functionality
    const showDeleteModal = ref(false)
    const selectedVersionToDelete = ref(null)
    const deleting = ref(false)

    const confirmDeleteVersion = version => {
      selectedVersionToDelete.value = version
      showDeleteModal.value = true
    }

    const deleteVersion = async () => {
      if (!selectedVersionToDelete.value) return

      try {
        deleting.value = true
        await axiosIns.delete(`/pricing/versions/${selectedVersionToDelete.value.id}`)
        successNotification('Version deleted successfully')
        showDeleteModal.value = false
        selectedVersionToDelete.value = null
        // Refetch the versions list
        await fetchVersions()
      } catch (error) {
        console.error('Error deleting version:', error)
        // Handle specific error messages from the API
        const errorMessage = error?.response?.data?.error?.detail?.message
          || 'Failed to delete version'
        errorNotification(errorMessage)
      } finally {
        deleting.value = false
      }
    }

    return {
      ...rest,
      versionsLoading,
      getVersionsList,
      showDeleteModal,
      selectedVersionToDelete,
      deleting,
      confirmDeleteVersion,
      deleteVersion,
    }
  },
}
</script>

  <style>

  </style>
